import Api from './Api'
import { EventType, SlotsResponse } from '../types'

export interface EventTypesResponse {
  event_types: EventType[]
  current_page: number
  total_pages: number
  total_entries: number
}
import SearchHelper from '../helpers/SearchHelper'

// Keys accepted by Api::EventTypesController#event_type_params (unpermitted params raise), plus host_booking_slug
export const EVENT_TYPE_WRITABLE_KEYS = [
  'title', 'slug', 'description', 'duration', 'active', 'durations', 'locations', 'before_event_buffer', 'after_event_buffer',
  'minimum_booking_notice', 'minimum_booking_notice_unit', 'slot_interval', 'booking_frequency_limit', 'booking_duration_limit',
  'period_type', 'period_days', 'period_count_calendar_days', 'period_start_date', 'period_end_date',
  'max_active_bookings_per_booker', 'only_first_slot_per_day', 'host_booking_slug',
] as const

const writableBody = (eventType: Record<string, any>) =>
  EVENT_TYPE_WRITABLE_KEYS.reduce((acc, key) => (key in eventType ? { ...acc, [key]: eventType[key] } : acc), {} as Record<string, any>)

export default class EventTypesController extends Api {
  private static headers() {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: this.token,
    }
  }

  static getEventTypes(params: object = {}): Promise<EventTypesResponse> {
    return fetch(`/api/event_types?${SearchHelper.getQueryString(params)}`, { headers: this.headers(), credentials: 'same-origin' }).then(res => res.json())
  }

  static getEventType(id: string): Promise<EventType> {
    return fetch(`/api/event_types/${id}`, { headers: this.headers(), credentials: 'same-origin' }).then(res => res.json())
  }

  static create(eventType: Partial<EventType>): Promise<EventType & { errors?: any }> {
    return fetch('/api/event_types', {
      method: 'POST', headers: this.headers(), credentials: 'same-origin', body: JSON.stringify({ event_type: writableBody(eventType) }),
    }).then(res => res.json())
  }

  static update(eventType: Partial<EventType> & { id: string, host_booking_slug?: string }): Promise<EventType & { errors?: any }> {
    return fetch(`/api/event_types/${eventType.id}`, {
      method: 'PATCH', headers: this.headers(), credentials: 'same-origin', body: JSON.stringify({ event_type: writableBody(eventType) }),
    }).then(res => res.json())
  }

  static delete(id: string): Promise<{ success?: boolean, error?: string }> {
    return fetch(`/api/event_types/${id}`, { method: 'DELETE', headers: this.headers(), credentials: 'same-origin' }).then(res => res.json())
  }

  static bulkDelete(eventTypeIds: string[]): Promise<{ success?: boolean }> {
    return fetch('/api/event_types/bulk_delete', {
      method: 'DELETE', headers: this.headers(), credentials: 'same-origin', body: JSON.stringify({ event_type_ids: eventTypeIds }),
    }).then(res => res.json())
  }

  static duplicate(id: string): Promise<EventType> {
    return fetch(`/api/event_types/${id}/duplicate`, { method: 'POST', headers: this.headers(), credentials: 'same-origin' }).then(res => res.json())
  }

  static getSlots(id: string, params: { from: string, to: string, timezone: string, duration?: number, overrides?: Partial<EventType> }): Promise<SlotsResponse> {
    const query = SearchHelper.getQueryString({
      ...params,
      overrides: params.overrides ? JSON.stringify(params.overrides) : undefined,
    })
    return fetch(`/api/event_types/${id}/slots?${query}`, { headers: this.headers(), credentials: 'same-origin' }).then(res => res.json())
  }
}
